// VEILLE MARCHÉ (R-VEI-01 à R-VEI-03) : vingt minutes par mois, en format faible énergie.
// On colle une dizaine d'offres de mission, le mentor en tire les compétences, et la carte des
// écarts dit ce que le programme ne couvre pas. Trois mois de suite, c'est un manque, pas un hasard
// (docs/formation-product-architect.md §10).
import { useState } from 'react';
import { useMagasin, magasin } from '../donnees/magasin';
import { ecartsPersistants } from '@partage/programme';
import type { Couverture } from '@partage/types';

const COUVERTURES: { valeur: Couverture; texte: string }[] = [
  { valeur: 'couverte', texte: 'Couverte' },
  { valeur: 'partielle', texte: 'Partielle' },
  { valeur: 'absente', texte: 'Absente' },
];

/** La carte des écarts (R-VEI-02) : compétence demandée → ce que le programme en fait. */
function CarteDesEcarts() {
  const { etat } = useMagasin();
  const lignes = Object.entries(etat.veille.ecarts).sort(([a], [b]) => a.localeCompare(b));
  const alertes = new Set(ecartsPersistants(etat).map((e) => e.competence));

  if (!lignes.length) return <p className="vide">La carte se remplira à ta première veille.</p>;

  return (
    <table>
      <thead><tr><th>Compétence</th><th>Couverture</th><th>Saison</th><th>Vue</th></tr></thead>
      <tbody>
        {lignes.map(([competence, ecart]) => (
          <tr key={competence}>
            <td>
              {competence}
              {alertes.has(competence) ? <> <span className="badge accent" title="Vue trois veilles de suite sans être couverte">3 mois de suite</span></> : null}
            </td>
            <td>
              <select
                value={ecart.couverture}
                onChange={(e) => void magasin.emettre('ecart_marque', { competence, couverture: e.target.value as Couverture, saison: ecart.saison })}
              >
                {COUVERTURES.map((c) => <option value={c.valeur} key={c.valeur}>{c.texte}</option>)}
              </select>
            </td>
            <td>
              <select
                value={ecart.saison ?? ''}
                onChange={(e) => void magasin.emettre('ecart_marque', { competence, couverture: ecart.couverture, saison: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">—</option>
                {[1, 2, 3, 4].map((n) => <option value={n} key={n}>Saison {n}</option>)}
              </select>
            </td>
            <td className="petit-texte doux">{ecart.vu_le.length} fois</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Veille() {
  const { etat } = useMagasin();
  const [offres, setOffres] = useState('');
  const [competences, setCompetences] = useState('');
  const [message, setMessage] = useState<string>();

  const derniere = etat.veille.entrees.at(-1);
  const alertes = ecartsPersistants(etat);

  async function ajouter() {
    if (!offres.trim()) return;
    await magasin.emettre('veille_ajoutee', {
      entree: crypto.randomUUID(),
      offres: offres.trim(),
      competences: competences.split('\n').map((c) => c.trim()).filter(Boolean),
    });
    setOffres('');
    setCompetences('');
    setMessage('Veille enregistrée. Demande au mentor d’ajuster la carte des écarts.');
  }

  return (
    <>
      <h2>Veille marché</h2>
      <p className="doux">
        Une fois par mois, vingt minutes : tu colles une dizaine d’offres de mission (Free-Work, Malt,
        LinkedIn), le mentor en extrait les compétences et les compare au programme. Ça compte pour ta
        régularité, ça ne consomme aucune séance.
      </p>

      {alertes.length ? (
        <div className="message erreur">
          <strong>Trois veilles de suite sans couverture :</strong>{' '}
          {alertes.map((a) => a.competence).join(', ')}. Demande au mentor un ajustement du programme —
          avec sa compensation, le plafond de 33 séances ne bouge pas.
        </div>
      ) : null}

      <div className="carte">
        <div className="ligne">
          <strong>Nouvelle veille</strong>
          {derniere ? <span className="badge">dernière le {derniere.le.slice(0, 10)}</span> : null}
        </div>
        <div className="champ">
          <label>Les offres, collées telles quelles</label>
          <textarea rows={8} value={offres} onChange={(e) => setOffres(e.target.value)} placeholder="Colle ici une dizaine d’annonces, séparées comme tu veux." />
        </div>
        <div className="champ">
          <label>Compétences repérées — une par ligne (le mentor t’aide à les tirer)</label>
          <textarea rows={5} value={competences} onChange={(e) => setCompetences(e.target.value)} placeholder={'MCP\nLangGraph\nAI Act\n…'} />
        </div>
        {message ? <div className="message">{message}</div> : null}
        <button className="principal" onClick={() => void ajouter()} disabled={!offres.trim()}>Enregistrer la veille</button>
      </div>

      <h3>Carte des écarts</h3>
      <CarteDesEcarts />

      {etat.veille.entrees.length ? (
        <>
          <h3>Historique</h3>
          {[...etat.veille.entrees].reverse().map((e) => (
            <details className="carte" key={e.id}>
              <summary>{e.le.slice(0, 10)} — {e.competences.length} compétence{e.competences.length > 1 ? 's' : ''} repérée{e.competences.length > 1 ? 's' : ''}</summary>
              <p className="petit-texte">{e.competences.join(' · ')}</p>
              <pre className="petit-texte" style={{ whiteSpace: 'pre-wrap', maxHeight: '18em', overflow: 'auto' }}>{e.offres}</pre>
            </details>
          ))}
        </>
      ) : null}
    </>
  );
}
